import React, { useState, useEffect } from 'react';
import { FiLoader, FiCheckCircle, FiAlertCircle, FiRefreshCw } from 'react-icons/fi';
import Modal from '../../../components/Modal';
import { crosswalkApi, questionnairesApi } from '../../../services/api';

const STATUS_COLORS = {
  running:      'text-amber-600 bg-amber-50',
  processing:   'text-amber-600 bg-amber-50',
  polling:      'text-amber-600 bg-amber-50',
  enriching:    'text-purple-600 bg-purple-50',
  saving:       'text-indigo-600 bg-indigo-50',
  completed:    'text-green-600 bg-green-50',
  no_proposals: 'text-slate-600 bg-slate-50',
  failed:       'text-red-600 bg-red-50',
};

const ACTIVE_STATUSES = ['running', 'processing', 'polling', 'enriching', 'saving'];

const CrosswalkDetailsModal = ({ isOpen, onClose, assessment }) => {
  const [qnrDetails, setQnrDetails] = useState([]);
  const [fetchLoading, setFetchLoading] = useState(false);

  useEffect(() => {
    if (!isOpen || !assessment) return;

    const init = async () => {
      setFetchLoading(true);
      setQnrDetails([]);

      try {
        // Fetch all questionnaires for this assessment
        const res = await questionnairesApi.getAll(1, 100, null, null, assessment.id);
        const items = res.items || res.data || (Array.isArray(res) ? res : []);

        // For each questionnaire, fetch proposals from DB (same as reviewer section)
        const results = await Promise.all(
          items.map(async (q) => {
            const qId = q.questionnaireId || q.id;
            const qName = q.questionnaireName || q.name;
            try {
              const response = await crosswalkApi.getProposals(assessment.id, qId);
              const proposals = response?.proposals || [];
              // Prefer the real status/job id persisted from the crosswalk API; fall back
              // to inferring from the proposal count when the DB has no status yet.
              const apiStatus = response?.status || (proposals.length > 0 ? 'completed' : 'no_proposals');
              return {
                questionnaireId: qId,
                questionnaireName: qName,
                proposalCount: proposals.length,
                status: apiStatus,
                jobId: response?.jobId || null,
                error: null,
              };
            } catch {
              return {
                questionnaireId: qId,
                questionnaireName: qName,
                proposalCount: 0,
                status: 'failed',
                jobId: null,
                error: 'Failed to fetch proposals',
              };
            }
          })
        );

        setQnrDetails(results);
      } catch {
        setQnrDetails([]);
      } finally {
        setFetchLoading(false);
      }
    };

    init();
  }, [isOpen, assessment?.id]); // eslint-disable-line react-hooks/exhaustive-deps

  const totalProposals   = qnrDetails.reduce((sum, q) => sum + q.proposalCount, 0);
  const completedCount   = qnrDetails.filter(q => q.status === 'completed').length;
  const noProposalCount  = qnrDetails.filter(q => q.status === 'no_proposals').length;
  const failedCount      = qnrDetails.filter(q => q.status === 'failed').length;

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title={`Crosswalk Details — ${assessment?.accountName || `Assessment #${assessment?.id}`}`}
      size="lg"
    >
      <div className="space-y-4">
        {/* Summary bar */}
        <div className="flex items-center gap-4 text-sm flex-wrap">
          {fetchLoading ? (
            <span className="inline-flex items-center gap-1.5 text-slate-500">
              <FiLoader className="animate-spin" /> Loading proposals…
            </span>
          ) : qnrDetails.length === 0 ? (
            <span className="text-slate-500">No questionnaires found for this assessment.</span>
          ) : (
            <span className="inline-flex items-center gap-1.5 text-green-600">
              <FiCheckCircle /> All jobs finished — {totalProposals} total proposal(s)
            </span>
          )}
          {completedCount  > 0 && <span className="text-green-600 font-medium">{completedCount} completed</span>}
          {noProposalCount > 0 && <span className="text-slate-500 font-medium">{noProposalCount} no proposals</span>}
          {failedCount     > 0 && <span className="text-red-600 font-medium">{failedCount} failed</span>}
        </div>

        {/* Questionnaire rows */}
        {qnrDetails.length > 0 && (
          <div className="divide-y divide-slate-100 border border-slate-200 rounded-lg">
            {qnrDetails.map((q, i) => (
              <div key={q.questionnaireId || i} className="flex items-start justify-between px-4 py-3 gap-3">
                <div className="flex-1 min-w-0">
                  <p className="text-sm font-medium text-slate-800">
                    {q.questionnaireName || `Questionnaire #${q.questionnaireId}`}
                  </p>
                  {q.error ? (
                    <p className="text-xs text-red-500 mt-0.5">{q.error}</p>
                  ) : (
                    <p className={`text-xs mt-0.5 ${q.proposalCount > 0 ? 'text-green-600' : 'text-slate-500'}`}>
                      {q.proposalCount} proposal(s)
                    </p>
                  )}
                  {q.jobId && (
                    <p className="text-[11px] text-slate-400 font-mono mt-0.5 truncate">
                      Job ID: {q.jobId}
                    </p>
                  )}
                </div>

                <span className={`flex-shrink-0 inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full text-xs font-medium ${STATUS_COLORS[q.status] || 'text-slate-600 bg-slate-50'}`}>
                  {ACTIVE_STATUSES.includes(q.status)
                    ? <><FiLoader className="animate-spin text-xs" /> {q.status}</>
                    : q.status === 'completed'
                      ? <><FiCheckCircle className="text-xs" /> completed</>
                      : q.status === 'failed'
                        ? <><FiAlertCircle className="text-xs" /> failed</>
                        : q.status === 'no_proposals'
                          ? <><FiRefreshCw className="text-xs" /> no proposals</>
                          : <>{q.status}</>}
                </span>
              </div>
            ))}
          </div>
        )}
      </div>
    </Modal>
  );
};

export default CrosswalkDetailsModal;
